import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MapPin, Star, Shield, ChevronLeft, Calendar, User, ChevronRight } from 'lucide-react';
import type { Tool } from '@tools-beaver/shared';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import toast from 'react-hot-toast';

export function ToolDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, openAuthModal } = useAppStore();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [imgIdx, setImgIdx] = useState(0);

  const { data: tool, isLoading } = useQuery<Tool>({
    queryKey: ['tool', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/tools/${id}`);
      return data.data;
    },
  });

  const requestMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/rentals', {
        toolId: id,
        startDate,
        endDate,
        borrowerNotes: notes,
      });
      return data.data;
    },
    onSuccess: (rental) => {
      toast.success('Rental request sent! 🎉');
      navigate(`/rentals/${rental.id}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? 'Failed to send request');
    },
  });

  const handleRequest = () => {
    if (!user) {
      openAuthModal('register');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    requestMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center text-silver-400">
        <div className="text-4xl animate-bounce-gentle mb-3">🔧</div>
        <p>Loading tool details…</p>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <p className="text-silver-500 mb-4">Tool not found.</p>
        <Link to="/browse" className="btn-primary">Browse Tools</Link>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const totalDays =
    startDate && endDate
      ? Math.ceil(
          (new Date(endDate).getTime() - new Date(startDate).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;
  const totalCost =
    totalDays > 0 ? (totalDays * Number(tool.pricePerDay)).toFixed(2) : null;
  const isOwner = user?.id === tool.ownerId;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-silver-500 hover:text-silver-800 mb-4 transition-colors"
      >
        <ChevronLeft size={16} /> Back
      </button>

      {/* Image gallery */}
      <div
        className="relative bg-silver-100 rounded-3xl overflow-hidden mb-6"
        style={{ height: 280 }}
      >
        {tool.images.length > 0 ? (
          <>
            <img
              src={tool.images[imgIdx]?.url}
              alt={tool.name}
              className="w-full h-full object-cover"
            />
            {tool.images.length > 1 && (
              <>
                <button
                  onClick={() => setImgIdx((i) => Math.max(0, i - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/30 rounded-full p-1.5 text-white"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setImgIdx((i) => Math.min(tool.images.length - 1, i + 1))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/30 rounded-full p-1.5 text-white"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {tool.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setImgIdx(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === imgIdx ? 'bg-white scale-125' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl text-silver-300">
            🔧
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className="badge bg-garden-500 text-white font-bold text-sm px-3 py-1.5">
            ${Number(tool.pricePerDay).toFixed(0)}/day
          </span>
        </div>
        {tool.status !== 'AVAILABLE' && (
          <div className="absolute top-3 left-3">
            <span className="badge bg-red-500 text-white">Unavailable</span>
          </div>
        )}
      </div>

      {/* Tool info */}
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-2xl text-silver-800 leading-tight mb-3">
          {tool.name}
        </h1>

        <div className="flex flex-wrap gap-2 mb-3">
          {tool.condition && (
            <span className="badge bg-silver-100 text-silver-600">
              {tool.condition.replace('_', ' ')}
            </span>
          )}
          {tool.category && (
            <span className="badge bg-garden-50 text-garden-700">
              {tool.category.icon} {tool.category.name}
            </span>
          )}
          {tool.city && (
            <span className="badge bg-beaver-cream text-beaver-brown flex items-center gap-1">
              <MapPin size={11} /> {tool.city}
              {tool.state ? `, ${tool.state}` : ''}
            </span>
          )}
          {tool.depositAmount ? (
            <span className="badge bg-blue-50 text-blue-700 flex items-center gap-1">
              <Shield size={11} /> ${Number(tool.depositAmount).toFixed(0)} deposit
            </span>
          ) : null}
        </div>

        {tool.brand && (
          <p className="text-silver-400 text-sm mb-3">
            {tool.brand}
            {tool.model ? ` · ${tool.model}` : ''}
          </p>
        )}
        <p className="text-silver-600 text-sm leading-relaxed">{tool.description}</p>

        {tool.minRentalDays > 1 && (
          <p className="text-xs text-silver-400 mt-2">
            Minimum rental: {tool.minRentalDays} days
          </p>
        )}
      </div>

      {/* Owner card */}
      {tool.owner && (
        <div className="card p-4 flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-silver-200 overflow-hidden flex-shrink-0">
            {tool.owner.avatarUrl ? (
              <img
                src={tool.owner.avatarUrl}
                alt={tool.owner.name ?? ''}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User size={18} className="text-silver-400" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-silver-800 text-sm">
              {tool.owner.name ?? 'Tool Owner'}
            </p>
            <div className="flex items-center gap-1 text-xs text-silver-400">
              <Star size={11} className="text-beaver-yellow fill-beaver-yellow" />
              <span>
                {Number(tool.owner.rating).toFixed(1)} ({tool.owner.reviewCount} reviews)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Terms */}
      {tool.termsAndConditions && (
        <div className="bg-silver-50 rounded-2xl p-4 mb-6">
          <p className="text-xs font-semibold text-silver-600 mb-1">Terms & Conditions</p>
          <p className="text-xs text-silver-500 leading-relaxed">{tool.termsAndConditions}</p>
        </div>
      )}

      {/* Rental request form */}
      {!isOwner && (
        <div className="card p-5">
          <h2 className="font-bold text-silver-800 mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-garden-500" />
            Request to Rent
          </h2>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-silver-600 mb-1.5">
                Start date
              </label>
              <input
                type="date"
                min={today}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-silver-600 mb-1.5">
                End date
              </label>
              <input
                type="date"
                min={startDate || today}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input text-sm"
              />
            </div>
          </div>

          {totalCost && (
            <div className="bg-garden-50 rounded-xl p-3 mb-3 flex justify-between items-center">
              <span className="text-sm text-garden-700">
                {totalDays} day{totalDays !== 1 ? 's' : ''} ×{' '}
                ${Number(tool.pricePerDay).toFixed(0)}
              </span>
              <span className="font-bold text-garden-700">${totalCost}</span>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-medium text-silver-600 mb-1.5">
              Message to lender{' '}
              <span className="text-silver-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Tell the lender what project you're working on…"
              className="input resize-none text-sm"
            />
          </div>

          <button
            onClick={handleRequest}
            disabled={requestMutation.isPending || tool.status !== 'AVAILABLE'}
            className="btn-primary w-full disabled:opacity-40"
          >
            {requestMutation.isPending
              ? 'Sending request…'
              : tool.status !== 'AVAILABLE'
              ? 'Tool Currently Unavailable'
              : '🦫 Request to Rent'}
          </button>

          {!user && (
            <p className="text-xs text-center text-silver-400 mt-3">
              You'll be asked to sign in before your request is sent
            </p>
          )}
        </div>
      )}

      {isOwner && (
        <div className="card p-4 text-center text-silver-500 text-sm">
          <p>This is your listing.</p>
          <Link to="/my-listings" className="text-garden-600 font-medium hover:underline">
            Manage your listings →
          </Link>
        </div>
      )}
    </div>
  );
}
